import React from 'react'

import { get, isEmpty } from 'lodash'

import httpClient, { catchError } from '@emmacare/common/api/httpClient'

import { getClinicians } from 'src/store/modules/common'
import {
  setActiveCompany,
  setAdminBillingPrograms,
  setCaseManagersByDivision,
  setCompanyDivision,
  setCompanyDivisionByCompany,
  setCompanyGoalCategories,
  setCompanyGoals,
  setCompanyLanguages,
  setCompanyModalLoading,
  setCompanyUserModalClinicians,
  setCompanyUsers,
  setRpmCompanyGoals,
  setUserEditRoles
} from 'src/store/modules/companiesAdministrating'

import {
  COMPANIES,
  COMPANY_USERS,
  CREATE_USER,
  DELETE_COMAPNY_IMAGE,
  GET_ADMIN_BILLING_PROGRAMS,
  GET_CM_BY_DIVISION,
  GET_COMPANY,
  GET_COMPANY_DIVISION,
  GET_COMPANY_DIVISION_BY_COMPANY,
  GET_COMPANY_GOAL,
  GET_COMPANY_GOAL_CATEGORY,
  GET_COMPANY_LANGUAGES,
  POST_COMPANY_BACKGROUND,
  SAVE_COMPANY_IMAGES,
  TREE_QUERY_PARAM,
  UNLOCK_USER,
  UPDATE_COMPANY,
  UPDATE_USER,
  UPLOAD_CAREGIVER_SHIFT,
  USER_ROLES
} from 'src/constants'
import { showErrorModal, showSuccessModal } from 'src/services/dialogs'

import { toggleAddEditModal } from './actionCreators'

export const createCompany = (payload, historyPush) => async (dispatch) => {
  dispatch(setCompanyModalLoading(true))
  try {
    const company = await httpClient.request({
      method: 'post',
      url: {
        pathname: COMPANIES
      },
      payload
    })
    await dispatch(toggleAddEditModal(false))
    await dispatch(setActiveCompany(company))
    historyPush(`/admin/${company.uuid}`)
    return false
  } catch (e) {
    catchError(e)
    return e
  } finally {
    dispatch(setCompanyModalLoading(false))
  }
}

export const updateCompany = (payload, uuid) => async (dispatch) => {
  dispatch(setCompanyModalLoading(true))

  try {
    const company = await httpClient.request({
      method: 'patch',
      url: {
        pathname: UPDATE_COMPANY,
        params: {
          uuid
        }
      },
      payload
    })
    dispatch(setActiveCompany(company))
    return false
  } catch (e) {
    catchError(e)
    return e
  } finally {
    dispatch(setCompanyModalLoading(false))
  }
}

export const getCompany =
  (config = {}) =>
  async (dispatch) => {
    const { params = {} } = config
    const companyUuid = params?.companyUuid

    try {
      const company = await httpClient.request({
        method: 'get',
        url: {
          pathname: GET_COMPANY,
          params
        }
      })

      await Promise.all([
        dispatch(
          getCompanyDivision({
            query: {
              ...TREE_QUERY_PARAM,
              'filter[parent]': 'null:',
              'filter[company]': `eq:${companyUuid}`
            }
          })
        ),
        dispatch(getCompanyLanguages(companyUuid))
      ])

      dispatch(setActiveCompany(company))
    } catch (err) {
      catchError(err)
    }
  }

export const fetchCompanyUsers =
  (config = {}) =>
  async (dispatch) => {
    const { params = {}, query = {} } = config

    try {
      const { items = [], meta = {} } = await httpClient.request({
        method: 'get',
        url: {
          pathname: COMPANY_USERS,
          params,
          query
        }
      })

      dispatch(setCompanyUsers(items))

      return meta
    } catch (err) {
      catchError(err)
    }
  }

export const createUser =
  (config = {}) =>
  async () => {
    const { payload = {} } = config

    try {
      await httpClient.request({
        method: 'post',
        url: {
          pathname: CREATE_USER
        },
        payload
      })

      return false
    } catch (e) {
      const { errors = {} } = e

      if (!isEmpty(errors)) {
        const content = (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {Object.keys(errors).map((key) => (
              <span
                style={{ textAlign: 'left', marginBottom: '10px' }}
                key={key}>
                {errors[key][0]}
                <br />
              </span>
            ))}
          </div>
        )

        showErrorModal({
          title: 'Validation Error',
          content
        })
      } else {
        catchError(e)
      }

      return true
    }
  }

export const updateUser = (config, uuid, isUnlock) => async (dispatch) => {
  const { payload = {} } = config

  try {
    if (isUnlock) {
      dispatch(unlockUser(uuid))
    }

    await httpClient.request({
      method: 'patch',
      url: {
        pathname: UPDATE_USER,
        params: {
          uuid
        }
      },
      payload
    })

    return false
  } catch (err) {
    catchError(err)

    return true
  }
}

export const getCompanyDivision =
  (config = {}) =>
  async (dispatch) => {
    const { query = {} } = config

    try {
      const { items } = await httpClient.request({
        method: 'get',
        url: {
          pathname: GET_COMPANY_DIVISION,
          query
        }
      })

      dispatch(setCompanyDivision(items))
    } catch (err) {
      catchError(err)
    }
  }

export const getCompanyDivisionByCompany =
  (config = {}) =>
  async (dispatch) => {
    const { query = {} } = config

    try {
      const { items } = await httpClient.request({
        method: 'get',
        url: {
          pathname: GET_COMPANY_DIVISION_BY_COMPANY,
          query
        }
      })

      dispatch(setCompanyDivisionByCompany(items))
    } catch (err) {
      catchError(err)
    }
  }

export const getCompanyLanguages = (uuid) => async (dispatch) => {
  try {
    const { items = [] } = await httpClient.request({
      method: 'get',
      url: {
        pathname: GET_COMPANY_LANGUAGES,
        params: {
          uuid
        }
      }
    })
    dispatch(setCompanyLanguages(items))
  } catch (err) {
    catchError(err)
  }
}

export const getUserRoles = (uuid) => async (dispatch) => {
  try {
    const items = await httpClient.request({
      method: 'get',
      url: {
        pathname: USER_ROLES,
        params: {
          uuid
        }
      }
    })
    dispatch(setUserEditRoles(items))
  } catch (err) {
    catchError(err)
  }
}

export const unlockUser = (uuid) => async () => {
  try {
    await httpClient.request({
      method: 'post',
      url: {
        pathname: UNLOCK_USER,
        params: { uuid }
      }
    })
    return false
  } catch (err) {
    catchError(err)
  }
}

export const cleanCompanyProfile = () => (dispatch) =>
  dispatch(setActiveCompany({}))

export const getCaseManagersByDivision =
  (config = {}) =>
  async (dispatch) => {
    const { query = {} } = config

    try {
      const { items } = await httpClient.request({
        method: 'get',
        url: {
          pathname: GET_CM_BY_DIVISION,
          query
        }
      })

      dispatch(setCaseManagersByDivision(items))

      return items
    } catch (err) {
      catchError(err)
    }
  }

export const uploadCaregiverShiftFile = async (config) => {
  const { params, data } = config

  try {
    const {
      data: { message }
    } = await httpClient.upload({
      url: {
        pathname: UPLOAD_CAREGIVER_SHIFT,
        params
      },
      data
    })

    if (message) {
      showSuccessModal({
        content: message,
        centered: true,
        keyboard: false
      })
    }
  } catch (err) {
    catchError(err)
  }
}

export const fetchCompanyUserModalClinicians =
  (config = {}) =>
  async (dispatch) => {
    try {
      const items = await getClinicians(config)

      dispatch(setCompanyUserModalClinicians(items))
    } catch (err) {
      catchError(err)
    }
  }

export const fetchAdminBillingPrograms =
  (config = {}) =>
  async (dispatch) => {
    const { params = {} } = config

    try {
      const codes = await httpClient.request({
        method: 'get',
        url: {
          pathname: GET_ADMIN_BILLING_PROGRAMS,
          params
        }
      })

      dispatch(setAdminBillingPrograms(codes))
    } catch (err) {
      catchError(err)
    }
  }

export const uploadCompanyImage =
  (config = {}, fieldName) =>
  async (dispatch) => {
    const { params = {}, data = {} } = config

    try {
      const {
        data: { uuid: imageUuid }
      } = await httpClient.upload({
        url: {
          pathname: POST_COMPANY_BACKGROUND,
          params
        },
        data
      })

      await saveCompanyBackground(
        get(params, ['uuid']),
        {
          callLogo:
            fieldName === 'callLogo'
              ? {
                  name: 'logo',
                  uuid: imageUuid
                }
              : null,
          pdfLogo:
            fieldName === 'pdfLogo'
              ? {
                  name: 'logo',
                  uuid: imageUuid
                }
              : null,

          callBackground:
            fieldName === 'callBackground'
              ? {
                  name: 'background',
                  uuid: imageUuid
                }
              : null,

          emrCallBackground:
            fieldName === 'emrCallBackground'
              ? {
                  name: 'background',
                  uuid: imageUuid
                }
              : null
        },
        dispatch
      )
    } catch (err) {
      catchError(err)

      return null
    }
  }

export const saveCompanyBackground = async (uuid, payload, dispatch) => {
  try {
    const company = await httpClient.request({
      method: 'patch',
      url: {
        pathname: SAVE_COMPANY_IMAGES,
        params: {
          uuid
        }
      },
      payload
    })

    dispatch(setActiveCompany(company))
  } catch (err) {
    catchError(err)
  }
}

export const deleteCompanyImage =
  (config = {}) =>
  async (dispatch) => {
    const { params = {} } = config
    const companyUuid = config?.params?.uuid

    try {
      await httpClient.delete({
        pathname: DELETE_COMAPNY_IMAGE,
        params
      })

      const config = {
        params: {
          companyUuid
        }
      }

      await dispatch(getCompany(config))
    } catch (err) {
      catchError(err)
    }
  }

export const getCompanyGoalCategories = (uuid) => async (dispatch) => {
  try {
    const { items = [] } = await httpClient.request({
      method: 'get',
      url: {
        pathname: GET_COMPANY_GOAL_CATEGORY,
        params: {
          uuid
        }
      }
    })
    dispatch(setCompanyGoalCategories(items))
  } catch (err) {
    catchError(err)
  }
}

export const getCompanyGoals = (config) => async (dispatch) => {
  const { params, query } = config
  try {
    const { items = [] } = await httpClient.request({
      method: 'get',
      url: {
        pathname: GET_COMPANY_GOAL,
        params,
        query
      }
    })
    dispatch(setCompanyGoals(items))
  } catch (err) {
    catchError(err)
  }
}
// TODO get rid of redux, use react-query
export const getRpmCompanyGoals = (config) => async (dispatch) => {
  const { params, query } = config
  try {
    const { items = [] } = await httpClient.request({
      method: 'get',
      url: {
        pathname: GET_COMPANY_GOAL,
        params,
        query
      }
    })
    dispatch(setRpmCompanyGoals(items))
  } catch (err) {
    catchError(err)
  }
}
